import { useQueryClient } from '@tanstack/react-query';
import { memo } from 'react';

import { type Form, type ShortUrlResponse } from '@air/api/types';
import { InfoIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { ContentCollectionAutoSubmitToggle } from '~/components/Modals/ContentCollectionModal/ContentCollectionAutoSubmitToggle';
import { ContentCollectionCopyLink } from '~/components/Modals/ContentCollectionModal/ContentCollectionCopyLink';
import { ContentCollectionCreateSubmissionBoardToggle } from '~/components/Modals/ContentCollectionModal/ContentCollectionCreateSubmissionBoardToggle';
import { ContentCollectionRequireAccountToggle } from '~/components/Modals/ContentCollectionModal/ContentCollectionRequireAccountToggle';
import { CustomFieldsSection } from '~/components/Modals/ContentCollectionModal/CustomFieldsSection';
import { FormDescription } from '~/components/Modals/ContentCollectionModal/FormDescription';
import { FormTitle } from '~/components/Modals/ContentCollectionModal/FormTitle';
import { SettingsItem } from '~/components/Modals/ContentCollectionModal/SettingsItem';
import { type MenuAction } from '~/components/UI/CopyField';
import { getPublicBoardCustomFieldsKey } from '~/constants/react-query-keys';
import { useFormPermissions } from '~/hooks/useFormPermissions';
import { useShortUrlPermissions } from '~/hooks/useShortUrlPermissions';
import { type EditShareLinkView } from '~/types/ShareLink';
import { canCreateShortUrl, canDeleteShortUrl } from '~/utils/permissions/shortUrlPermissions';

//currently this modal is only ever opened from the share button in a board header
const trackLocation = 'header';

type ContentCollectionEditActionsProps = {
  form: Form;
  shortIdInfo: ShortUrlResponse;
  setView: (view: EditShareLinkView) => void;
};

export const ContentCollectionEditActions = memo(
  ({ form, shortIdInfo, setView }: ContentCollectionEditActionsProps) => {
    const queryClient = useQueryClient();
    const _fields = form.fields;
    // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
    const formFields = _fields?.filter((field) => field.type !== 'custom_field' && field.type !== 'title');
    const { formPermissions } = useFormPermissions({
      formId: form.id,
    });

    const { shortUrlPermissions } = useShortUrlPermissions({ shortId: shortIdInfo.id });

    const menuActions: MenuAction[] = [];

    if (canDeleteShortUrl(shortUrlPermissions)) {
      menuActions.push({
        text: 'Delete link',
        action: () => setView('delete'),
      });

      if (canCreateShortUrl(formPermissions)) {
        menuActions.push({
          text: 'Generate a new link',
          action: () => setView('regenerate'),
        });
      }
    }

    return (
      <>
        <div className="flex flex-col gap-2" data-testid="CONTENT_COLLECTION_FORM_DETAILS_EDIT">
          <div className="flex flex-col gap-2">
            <div className="font-medium ads:text-metadata text-14 text-gray-10">Form details</div>
            <FormTitle form={form} />
            <FormDescription form={form} />
          </div>

          <ContentCollectionAutoSubmitToggle form={form} />
        </div>
        <div className={cn('flex flex-col gap-2', form.autoSubmit && 'pointer-events-none opacity-40')}>
          <div className="flex items-center gap-1 ads:text-icon-default text-gray-10">
            <div className="font-medium ads:text-metadata text-14 text-gray-10">Form fields</div>
            <Tooltip
              className="w-72"
              label="Add fields to your form so visitors can categorize their uploads. Require certain fields, or leave them optional."
            >
              <InfoIcon size="sm" />
            </Tooltip>
          </div>
          <div className="flex flex-col gap-2">
            {/* biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration */}
            {formFields?.map((formField) => (
              <SettingsItem form={form} key={formField.id} formField={formField} />
            ))}
            <CustomFieldsSection
              form={form}
              fields={_fields}
              // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
              onCreate={() =>
                queryClient.invalidateQueries({ queryKey: getPublicBoardCustomFieldsKey(form.boardId, shortIdInfo.id) })
              }
              trackLocation={trackLocation}
            />
          </div>
        </div>

        <div className="h-px ads:bg-ads-gray-5 bg-gray-5" />
        <div className="flex flex-col gap-2">
          <ContentCollectionRequireAccountToggle boardId={form.boardId} formId={form.id} shortIdInfo={shortIdInfo} />
          <ContentCollectionCreateSubmissionBoardToggle form={form} />
        </div>

        <div className="flex flex-col gap-3">
          <ContentCollectionCopyLink shortIdInfo={shortIdInfo} menuActions={menuActions} />
        </div>
      </>
    );
  },
);

ContentCollectionEditActions.displayName = 'ContentCollectionEditActions';
